import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  name: string;
  status: 'active' | 'maintenance' | 'inactive';
  lastUpdate: string;
  nextMaintenance: string;
  description: string;
}

export default function ServicesList() {
  const services: Service[] = [
    {
      id: 1,
      name: "Managed IT Infrastructure",
      status: "active",
      lastUpdate: "Jan 24, 2026",
      nextMaintenance: "Feb 10, 2026",
      description: "24/7 monitoring and management of your IT infrastructure"
    },
    {
      id: 2,
      name: "Cloud Backup Solutions",
      status: "active",
      lastUpdate: "Jan 25, 2026",
      nextMaintenance: "Feb 5, 2026",
      description: "Automated daily backups with 99.9% reliability"
    },
    {
      id: 3,
      name: "Cybersecurity Suite",
      status: "active",
      lastUpdate: "Jan 23, 2026",
      nextMaintenance: "Feb 1, 2026",
      description: "Advanced threat protection and security monitoring"
    },
    {
      id: 4,
      name: "Email Security",
      status: "maintenance",
      lastUpdate: "Jan 20, 2026",
      nextMaintenance: "Jan 26, 2026",
      description: "Spam filtering and email threat protection"
    }
  ];

  const getStatusColor = (status: Service['status']) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'maintenance':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'inactive':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusText = (status: Service['status']) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'maintenance':
        return 'Maintenance';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="px-6 py-4 border-b border-border bg-muted/30">
        <h2 className="text-xl font-headline font-bold text-primary">Active Services</h2>
        <p className="text-sm text-muted-foreground mt-1">Manage and monitor your IT services</p>
      </div>
      
      <div className="divide-y divide-border">
        {services.map((service) => (
          <div key={service.id} className="p-6 hover:bg-muted/30 transition-colors duration-200">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-headline font-semibold text-primary">
                    {service.name}
                  </h3>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${getStatusColor(service.status)}`}>
                    {getStatusText(service.status)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={14} variant="outline" />
                    <span>Last Update: {service.lastUpdate}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="WrenchScrewdriverIcon" size={14} variant="outline" />
                    <span>Next Maintenance: {service.nextMaintenance}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button className="px-4 py-2 text-sm font-cta font-semibold text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors duration-200 flex items-center gap-2">
                  <Icon name="Cog6ToothIcon" size={16} variant="outline" />
                  <span>Manage</span>
                </button>
                <button className="px-4 py-2 text-sm font-cta font-semibold text-muted-foreground hover:bg-muted rounded-lg transition-colors duration-200 flex items-center gap-2">
                  <Icon name="ChartBarIcon" size={16} variant="outline" />
                  <span>Details</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}